import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Divider, Switch,
} from '@mui/material';
import { C } from '../theme';

const API = process.env.REACT_APP_API_URL || 'https://api.binko.me';

function venueHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + localStorage.getItem('binko_venue_token'),
  };
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: '28px' }}>
      <Typography sx={{
        fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: C.textDisabled, mb: '12px',
      }}>
        {title}
      </Typography>
      <Box sx={{
        background: C.bgCard, borderRadius: '16px',
        border: '0.5px solid ' + C.border, overflow: 'hidden',
      }}>
        {children}
      </Box>
    </Box>
  );
}

function Row({ label, sublabel, right, divider = true }) {
  return (
    <>
      <Box sx={{
        px: '18px', py: '14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '12px',
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: C.textPrimary }}>
            {label}
          </Typography>
          {sublabel && (
            <Typography sx={{ fontSize: '12px', color: C.textMuted, mt: '2px', lineHeight: 1.4 }}>
              {sublabel}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{right}</Box>
      </Box>
      {divider && <Divider sx={{ borderColor: C.border, mx: '18px' }} />}
    </>
  );
}

function ToggleRow({ label, sublabel, value, onChange, loading, divider = true }) {
  return (
    <Row
      label={label}
      sublabel={sublabel}
      divider={divider}
      right={
        loading
          ? <CircularProgress size={20} sx={{ color: C.teal }} />
          : (
            <Switch
              checked={!!value}
              onChange={e => onChange(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: C.teal },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.teal },
              }}
            />
          )
      }
    />
  );
}

export default function VenueSettings() {
  const venueName = localStorage.getItem('binko_venue_name') || 'Venue';

  const [venue,       setVenue]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [checkinMode, setCheckinMode] = useState('auto');
  const [modeLoading, setModeLoading] = useState(false);
  const [modeError,   setModeError]   = useState('');
  const [qrEnabled,   setQrEnabled]   = useState(true);
  const [qrLoading,   setQrLoading]   = useState(false);
  const [qrError,     setQrError]     = useState('');
  const [toast,       setToast]       = useState('');

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }

  const fetchVenue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(API + '/api/venue/me', { headers: venueHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setVenue(data.venue);
      setCheckinMode(data.venue.checkin_mode || 'auto');
      setQrEnabled(!!data.venue.qr_entry_enabled);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVenue(); }, [fetchVenue]);

  async function handleModeChange(newMode) {
    if (newMode === checkinMode || modeLoading) return;
    setModeLoading(true);
    setModeError('');
    try {
      const res  = await fetch(API + '/api/venue/mode', {
        method: 'PATCH',
        headers: venueHeaders(),
        body: JSON.stringify({ mode: newMode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setCheckinMode(newMode);
      showToast('Check-in mode updated');
    } catch (err) {
      setModeError(err.message);
    } finally {
      setModeLoading(false);
    }
  }

  async function handleQrToggle(enabled) {
    setQrLoading(true);
    setQrError('');
    try {
      const res  = await fetch(API + '/api/venue/qr-toggle', {
        method: 'PATCH',
        headers: venueHeaders(),
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setQrEnabled(enabled);
      showToast('QR check-in ' + (enabled ? 'enabled' : 'disabled'));
    } catch (err) {
      setQrError(err.message);
    } finally {
      setQrLoading(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem('binko_venue_token');
    localStorage.removeItem('binko_venue_name');
    window.history.pushState({}, '', '/venue/login');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  function goTo(p) {
    window.history.pushState({}, '', p);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  return (
    <Box sx={{ minHeight: '100vh', background: C.bgApp, display: 'flex', flexDirection: 'column' }}>

      <Box sx={{
        background: C.bgCard, borderBottom: '0.5px solid ' + C.border,
        px: '20px', py: '14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Binko" style={{ height: '28px', width: 'auto' }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: C.textPrimary }}>
            {venueName}
          </Typography>
        </Box>
        <Button onClick={() => goTo('/venue/dashboard')}
          sx={{ fontSize: '12px', textTransform: 'none', color: C.textMuted, minWidth: 0 }}>
          ← Dashboard
        </Button>
      </Box>

      <Box sx={{ flex: 1, px: '20px', pt: '24px', pb: '40px', maxWidth: '560px', mx: 'auto', width: '100%' }}>

        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: C.textPrimary, mb: '24px' }}>
          Settings
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '60px' }}>
            <CircularProgress size={28} sx={{ color: C.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{
            background: 'rgba(254,114,16,0.08)', border: '0.5px solid ' + C.orange,
            borderRadius: '12px', px: '16px', py: '14px', mb: '20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Typography sx={{ fontSize: '13px', color: C.orange }}>{error}</Typography>
            <Button onClick={fetchVenue} sx={{ fontSize: '12px', color: C.orange, textTransform: 'none', minWidth: 0 }}>
              Retry
            </Button>
          </Box>
        )}

        {!loading && venue && (
          <>
            <Section title="Venue Info">
              <Row
                label="Venue name"
                right={<Typography sx={{ fontSize: '14px', color: C.textMuted }}>{venue.name}</Typography>}
              />
              <Row
                label="Logo"
                sublabel="Set via SQL by Binko admin"
                divider={false}
                right={
                  venue.logo_url
                    ? <img src={venue.logo_url} alt="Logo" style={{ height: 40, width: 'auto', borderRadius: 6, opacity: 0.85 }} />
                    : <Typography sx={{ fontSize: '12px', color: C.textDisabled }}>Not set</Typography>
                }
              />
            </Section>

            <Section title="Check-in Mode">
              <Row
                label="Mode"
                sublabel={checkinMode === 'auto' ? 'Users check in instantly' : 'Staff must approve each request'}
                divider={!!modeError}
                right={
                  <Box sx={{ display: 'flex', gap: '6px' }}>
                    {['auto', 'manual'].map(m => (
                      <Button key={m} disabled={modeLoading} onClick={() => handleModeChange(m)}
                        sx={{
                          fontSize: '12px', textTransform: 'capitalize', borderRadius: '8px', px: '12px', py: '5px',
                          background: checkinMode === m ? C.yellow : 'transparent',
                          color: checkinMode === m ? '#000' : C.textMuted,
                          border: '0.5px solid ' + (checkinMode === m ? C.yellow : C.border),
                          fontWeight: checkinMode === m ? 700 : 400,
                          minWidth: '60px',
                          '&:hover': { background: checkinMode === m ? C.yellow : 'rgba(255,255,255,0.05)' },
                          '&:disabled': { opacity: 0.5 },
                        }}>
                        {m}
                      </Button>
                    ))}
                  </Box>
                }
              />
              {modeError && (
                <Typography sx={{ fontSize: '12px', color: C.orange, px: '18px', pb: '12px' }}>{modeError}</Typography>
              )}
            </Section>

            <Section title="QR Entry">
              <ToggleRow
                label="QR check-in enabled"
                sublabel="Users scan QR to check in without GPS"
                value={qrEnabled}
                onChange={handleQrToggle}
                loading={qrLoading}
              />
              {qrError && (
                <Typography sx={{ fontSize: '12px', color: C.orange, px: '18px', pb: '12px' }}>{qrError}</Typography>
              )}
              <Row
                label="QR display screen"
                sublabel="Fullscreen QR for projection at entrance"
                divider={false}
                right={
                  <Button onClick={() => goTo('/venue/qr')}
                    sx={{
                      fontSize: '12px', textTransform: 'none', color: C.teal,
                      border: '0.5px solid ' + C.teal, borderRadius: '8px', px: '12px', py: '5px',
                      '&:hover': { background: 'rgba(13,157,165,0.1)' },
                    }}>
                    Open
                  </Button>
                }
              />
            </Section>

            <Section title="Screens">
              <Row
                label="Tournament Spectator"
                sublabel="Live tournament view for TV projection"
                right={
                  <Button onClick={() => goTo('/venue/spectator')}
                    sx={{
                      fontSize: '12px', textTransform: 'none', color: C.teal,
                      border: '0.5px solid ' + C.teal, borderRadius: '8px', px: '12px', py: '5px',
                      '&:hover': { background: 'rgba(13,157,165,0.1)' },
                    }}>
                    Open
                  </Button>
                }
              />
              <Row
                label="QR Code Display"
                sublabel="Fullscreen QR for entrance"
                divider={false}
                right={
                  <Button onClick={() => goTo('/venue/qr')}
                    sx={{
                      fontSize: '12px', textTransform: 'none', color: C.teal,
                      border: '0.5px solid ' + C.teal, borderRadius: '8px', px: '12px', py: '5px',
                      '&:hover': { background: 'rgba(13,157,165,0.1)' },
                    }}>
                    Open
                  </Button>
                }
              />
            </Section>

            <Section title="Account">
              <Row
                label="Signed in as"
                right={<Typography sx={{ fontSize: '13px', color: C.textMuted }}>{venue.name}</Typography>}
              />
              <Row
                label="Sign out"
                sublabel="You will need your OTP to sign back in"
                divider={false}
                right={
                  <Button onClick={handleLogout}
                    sx={{
                      fontSize: '12px', textTransform: 'none', color: C.orange,
                      border: '0.5px solid ' + C.orange, borderRadius: '8px', px: '12px', py: '5px',
                      '&:hover': { background: 'rgba(254,114,16,0.1)' },
                    }}>
                    Sign out
                  </Button>
                }
              />
            </Section>
          </>
        )}
      </Box>

      {toast && (
        <Box sx={{
          position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
          background: C.bgElevated, border: '0.5px solid ' + C.border,
          borderRadius: '20px', px: '20px', py: '10px', zIndex: 100,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}>
          <Typography sx={{ fontSize: '13px', color: C.success, fontWeight: 600 }}>
            ✓ {toast}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
